import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { isWeb, navigateTo, navigationRef, Popup } from '@helpers';
import { setPreImageHandlingScreen } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import ImageHandling from '@v2/components/imageHandling';
import { ImageHandlingDisplayMode } from '@v2/components/imageHandling/types/types.d';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { IMAGE_HANDLING_POPUP_ID } from '@v2/components/imageHandling/constants';

export const useHandleImageNavigation = () => {
  const dispatch = useDispatch();
  const previousParamsRef = useRef<Record<string, unknown> | null>(null);
  const popupRouteKeyRef = useRef<string | null>(null);

  const handleImageNavigation = (params: Record<string, unknown>) => {
    trackEvent(TRACKING_EVENTS.DOCUMENT, { action: 'viewed' });
    const currentRoute = navigationRef?.current?.getCurrentRoute();

    // store screen from which we are navigating to the ImageHandling to return to it later
    if (currentRoute) {
      dispatch(setPreImageHandlingScreen(currentRoute));
    }

    if (!isWeb) {
      navigateTo('ImageHandling', params);
      return;
    }

    // Save current params before setting new ones
    if (navigationRef.current) {
      const openRoute = navigationRef.current.getCurrentRoute();
      const currentParams = openRoute?.params || {};
      previousParamsRef.current = currentParams;
      popupRouteKeyRef.current = openRoute?.key ?? null;

      // Set new params in navigation ref for compatibility
      navigationRef.current.setParams({ ...params, imageHandlingMode: params?.imageHandlingMode ?? ImageHandlingDisplayMode.PICTURE });
    }

    // Create a wrapper component that will restore params on unmount
    const ImageHandlingWrapper = (props: any) => {
      // When component unmounts (modal closes), restore previous params
      React.useEffect(() => {
        return () => {
          if (!navigationRef.current) return;

          // If we navigated away from the route the popup opened over, do NOT touch
          // the new route's params — restoring the previous route's params here would
          // overwrite the new screen's params (and strip them from the URL).
          const nowRoute = navigationRef.current.getCurrentRoute();
          if (nowRoute?.key !== popupRouteKeyRef.current) {
            previousParamsRef.current = null;
            popupRouteKeyRef.current = null;
            return;
          }

          const currentParams = nowRoute?.params || {};
          const previousParams = previousParamsRef.current || {};

          // Find keys that exist in current params but not in previous params
          const keysToRemove: Record<string, undefined> = {};
          Object.keys(currentParams).forEach((key) => {
            if (!(key in previousParams)) {
              keysToRemove[key] = undefined;
            }
          });

          // First restore previous params
          if (previousParamsRef.current) {
            navigationRef.current.setParams(previousParamsRef.current);
          }

          // Then set new keys to undefined
          if (Object.keys(keysToRemove).length > 0) {
            navigationRef.current.setParams(keysToRemove);
          }

          previousParamsRef.current = null;
          popupRouteKeyRef.current = null;
        };
      }, []);

      return <ImageHandling {...props} />;
    };

    // Open ImageHandling component in Popup modal (full screen)
    Popup({
      Component: ImageHandlingWrapper,
      id: IMAGE_HANDLING_POPUP_ID,
      bottomSheet: false, // Not a bottom sheet, full screen instead
      useFullHeight: true,
      shouldCloseOnPressOutside: false,
      animationIn: 'slideInUp',
      animationOut: 'slideOutDown',
      borderRadius: 0,
      width: '100%', // Full width
      offsetTop: 0, // No offset from top
      offsetBottom: 0, // No offset from bottom
      paddingHorizontal: 0, // No horizontal padding
      centered: false, // Don't center, use full screen
      centeredHorizontally: false,
      centeredVertically: false,
    });
  };

  return { handleImageNavigation };
};
